import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import {
  ProposalCategory,
  ProposalExecutionType
} from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import {
  useCreateProposalMutation,
  useGetDaoEligibilityQuery
} from 'state/services/indexNode/indexNode'
import useToast from 'hooks/useToast'

const FormShell = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 760px;
`

const FieldLabel = styled(Text)`
  margin-bottom: 4px;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const Textarea = styled.textarea`
  width: 100%;
  min-height: 280px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const EligibilityCallout = styled(Flex)<{ $ok: boolean }>`
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid
    ${({ theme, $ok }) => ($ok ? theme.colors.green : theme.colors.red)};
  background: ${({ theme, $ok }) =>
    $ok ? `${theme.colors.green}11` : `${theme.colors.red}11`};
`

const NewProposalForm: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = account ? lifetimeTokens?.[account] : undefined
  const { toastSuccess, toastError } = useToast()

  const [title, setTitle] = useState('')
  const [bodyMarkdown, setBodyMarkdown] = useState('')
  const [category, setCategory] = useState<ProposalCategory>(
    ProposalCategory.GOVERNANCE
  )
  const [executionType, setExecutionType] = useState<ProposalExecutionType>(
    ProposalExecutionType.OFF_CHAIN
  )
  const [submitting, setSubmitting] = useState(false)

  const { data: eligibility, isLoading: eligibilityLoading } =
    useGetDaoEligibilityQuery(
      { account: account ?? '', accessToken: accessToken ?? '' },
      { skip: !account || !accessToken, refetchOnMountOrArgChange: true }
    )

  const [createProposal] = useCreateProposalMutation()

  const formValid = useMemo(
    () =>
      title.trim().length >= 4 && bodyMarkdown.trim().length >= 10,
    [title, bodyMarkdown]
  )

  const onSubmit = useCallback(async () => {
    if (!account || !accessToken) {
      toastError(t('dao.signinFirst'))
      return
    }
    if (!eligibility?.ok) {
      toastError(t('dao.notEligible'))
      return
    }
    setSubmitting(true)
    try {
      const created = await createProposal({
        account,
        accessToken,
        title: title.trim(),
        bodyMarkdown,
        category,
        executionType
      }).unwrap()
      toastSuccess(t('dao.proposalCreated'))
      navigate(`/dao/proposals/${created.proposalId}`)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      toastError(t('dao.createFailed', { reason: msg }))
    } finally {
      setSubmitting(false)
    }
  }, [
    account,
    accessToken,
    eligibility,
    createProposal,
    title,
    bodyMarkdown,
    category,
    executionType,
    navigate,
    toastError,
    toastSuccess,
    t
  ])

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px" gap="16px">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => navigate('/dao')}
        >
          ← {t('dao.backToList')}
        </Button>

        <Text variant={TextVariant.H4}>{t('dao.newProposalTitle')}</Text>
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('dao.newProposalDescription')}
        </Text>

        {!account || !accessToken ? (
          <EligibilityCallout $ok={false}>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
              {t('dao.signinFirst')}
            </Text>
          </EligibilityCallout>
        ) : eligibilityLoading ? (
          <Text color={colors.gray6}>{t('dao.checkingEligibility')}</Text>
        ) : eligibility ? (
          <EligibilityCallout $ok={eligibility.ok}>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
              {eligibility.ok
                ? t('dao.eligibleToPropose')
                : t('dao.notEligible')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.eligibilityDetails', {
                inode: eligibility.details.inodeCount,
                mpro: eligibility.details.mproUnits.toLocaleString(),
                minInode: eligibility.details.minInodeCount,
                minMpro: eligibility.details.minMproUnits.toLocaleString(),
                mode: eligibility.details.thresholdMode
              })}
            </Text>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('dao.snapshotBlockHint', {
                block: eligibility.details.snapshotBlock
              })}
            </Text>
          </EligibilityCallout>
        ) : null}

        <FormShell>
          <Flex flexDirection="column" gap="6px">
            <FieldLabel variant={TextVariant.BODY_SMALL_BOLD}>
              {t('dao.fields.title')}
            </FieldLabel>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={t('dao.fields.titlePlaceholder')}
            />
          </Flex>

          <Flex gap="12px" flexWrap="wrap">
            <Flex flexDirection="column" gap="6px" style={{ flex: '1 1 240px' }}>
              <FieldLabel variant={TextVariant.BODY_SMALL_BOLD}>
                {t('dao.fields.category')}
              </FieldLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProposalCategory)}
              >
                {Object.values(ProposalCategory).map((c) => (
                  <option key={c} value={c}>
                    {t(`dao.category.${c}`)}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex flexDirection="column" gap="6px" style={{ flex: '1 1 240px' }}>
              <FieldLabel variant={TextVariant.BODY_SMALL_BOLD}>
                {t('dao.fields.executionType')}
              </FieldLabel>
              <Select
                value={executionType}
                onChange={(e) =>
                  setExecutionType(e.target.value as ProposalExecutionType)
                }
              >
                {Object.values(ProposalExecutionType).map((x) => (
                  <option key={x} value={x}>
                    {t(`dao.executionType.${x}`)}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap="6px">
            <FieldLabel variant={TextVariant.BODY_SMALL_BOLD}>
              {t('dao.fields.body')} ({t('dao.fields.markdown')})
            </FieldLabel>
            <Textarea
              value={bodyMarkdown}
              onChange={(e) => setBodyMarkdown(e.target.value)}
              maxLength={20000}
              placeholder={t('dao.fields.bodyPlaceholder')}
            />
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {bodyMarkdown.length} / 20000
            </Text>
          </Flex>

          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={!formValid || !eligibility?.ok || submitting}
            onClick={() => void onSubmit()}
          >
            {submitting ? t('dao.submitting') : t('dao.submitProposal')}
          </Button>
        </FormShell>
      </Flex>
    </MainWrapper>
  )
}

export default NewProposalForm
